/**
 * RatSelector — capability-aware Radio Access Technology picker.
 *
 * Used in three places:
 *   - Quick Tests page (`ResourceSelectors`)
 *   - Saved-flow execution wizard (`Step2Locations`)
 *   - TLAM modal (inherited via QuickTestsPage `AutoStartConfig`)
 *
 * Options not in `availableRats` are still rendered but disabled, with
 * a "(no capable modem at this location)" caption — chosen over hiding
 * so the user understands why they can't pick 5G at an EG25-only site.
 * `auto` is always enabled.
 */

import { Radio } from 'lucide-react';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import {
  FORCED_RAT_LABELS,
  FORCED_RAT_VALUES,
  type ForcedRat,
} from '@simcheck/shared-types';

export interface RatSelectorProps {
  value: ForcedRat;
  onChange: (rat: ForcedRat) => void;
  /**
   * Set of RATs (excluding 'auto') that at least one online modem at the
   * selected location supports. Use `availableRatsAtLocation` to compute
   * it from a list of modems. `null` = "we don't know yet" → all RATs
   * enabled (avoids flicker before the modem list loads).
   */
  availableRats: Set<Exclude<ForcedRat, 'auto'>> | null;
  /**
   * Optional country-scoped availability map. When provided, options that
   * aren't supported at every location in the country render with a small
   * "supported at N of M locations" caption rather than a generic
   * "no capable modem" line. Used by the country-first Quick Tests UX.
   */
  availabilityByRat?: Map<
    Exclude<ForcedRat, 'auto'>,
    { supportedLocationIds: string[]; totalLocations: number }
  > | null;
  disabled?: boolean;
  /** Hide the leading icon + label (when the surrounding form already labels). */
  inline?: boolean;
}

export function RatSelector({
  value,
  onChange,
  availableRats,
  availabilityByRat,
  disabled,
  inline,
}: RatSelectorProps) {
  return (
    <div className={inline ? 'space-y-1' : 'space-y-2'}>
      {!inline && (
        <div className="flex items-center gap-2">
          <Radio className="w-4 h-4 text-muted-foreground" />
          <Label className="text-sm text-muted-foreground">
            Force network type (optional)
          </Label>
        </div>
      )}
      <Select
        value={value}
        onValueChange={(v) => onChange(v as ForcedRat)}
        disabled={disabled}
      >
        <SelectTrigger className="w-full text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {FORCED_RAT_VALUES.map((rat) => {
            // 'auto' always enabled. Others gated by capability.
            const supportable =
              rat === 'auto' ||
              availableRats === null ||
              availableRats.has(rat);
            const label = FORCED_RAT_LABELS[rat];
            const countryCoverage =
              rat !== 'auto' && availabilityByRat
                ? availabilityByRat.get(rat as Exclude<ForcedRat, 'auto'>) ?? null
                : null;
            // Country-scope caption: only render when the picker is in
            // country mode AND the RAT isn't supported at every location.
            // Equal counts → no caption (avoid noise on the universal RATs).
            const showPartialCoverage =
              !!countryCoverage &&
              supportable &&
              countryCoverage.totalLocations > 0 &&
              countryCoverage.supportedLocationIds.length > 0 &&
              countryCoverage.supportedLocationIds.length < countryCoverage.totalLocations;
            return (
              <SelectItem
                key={rat}
                value={rat}
                disabled={!supportable}
              >
                <span>
                  {label}
                  {!supportable && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {availabilityByRat
                        ? '(no capable modem in this country)'
                        : '(no capable modem at this location)'}
                    </span>
                  )}
                  {showPartialCoverage && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      (supported at {countryCoverage!.supportedLocationIds.length} of{' '}
                      {countryCoverage!.totalLocations} locations)
                    </span>
                  )}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}
